import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  CURRENCIES, emptyLine, emptyRequestForm, formTotalMinor, lineMinor, money, newKey, procurementError, requestFormFrom, requestFormProblem, requestPayload,
} from "../lib/procurement";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// One page for the two jobs: a new request, or the correction of one that is still editable (draft, refused, submitted, approved).
export default function ProcurementRequestForm() {
  const { requestId } = useParams();
  const editing = !!requestId;
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyRequestForm);
  const [original, setOriginal] = useState(null);
  const [phase, setPhase] = useState(editing ? "loading" : "ready");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const [currencies, setCurrencies] = useState(CURRENCIES);

  // The currencies Paramètres › Achats accepts; the request's own currency stays selectable when editing an older one.
  useEffect(() => {
    api.get("/procurement/request-options").then(({ data }) => setCurrencies(data.currencies)).catch(() => setCurrencies(CURRENCIES));
  }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/procurement/requests/${requestId}`)
      .then(({ data }) => {
        if (!data.capabilities.edit) { setPhase("locked"); return; }
        setOriginal(data);
        setForm(requestFormFrom(data));
        setPhase("ready");
      })
      .catch((e) => { setError(procurementError(e, "Impossible de charger la demande.")); setPhase("error"); });
  }, [editing, requestId]);

  const change = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const setLine = (index, patch) => change({ lines: form.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) });
  const problem = requestFormProblem(form);
  const total = formTotalMinor(form);
  const quotesLocked = !!original && original.quotes.length > 0; // the currency is the quotes' too

  const submit = async (event) => {
    event.preventDefault();
    if (problem) return;
    setBusy(true);
    setError("");
    try {
      const body = requestPayload(form);
      const { data } = editing
        ? await api.patch(`/procurement/requests/${requestId}`, { ...body, expected_version: original.version })
        : await api.post("/procurement/requests", body, { headers: { "Idempotency-Key": key } });
      toast.success(editing ? "Demande modifiée" : "Demande créée : ajoutez maintenant un devis");
      navigate(`/achats/demandes/${data.id}`);
    } catch (e) {
      setError(procurementError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "error" || phase === "locked") {
    return (
      <Card className="p-6 text-center space-y-3 max-w-xl mx-auto" data-testid="request-form-blocked">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{phase === "locked" ? "Cette demande n'est plus modifiable : elle est commandée, terminée ou annulée, ou ce n'est pas la vôtre." : error}</p>
        <Button variant="outline" asChild><Link to={editing ? `/achats/demandes/${requestId}` : "/achats/demandes"}>Retour</Link></Button>
      </Card>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="request-form-page">
      <div>
        <Link to={editing ? `/achats/demandes/${requestId}` : "/achats/demandes"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft size={14} />{editing ? original.number : "Demandes d'achat"}
        </Link>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1">{editing ? "Modifier la demande" : "Nouvelle demande d'achat"}</h1>
        <p className="text-sm text-slate-500">
          {editing && original.status === "approved" ? "Changer le montant, la devise ou les lignes annule l'approbation : la demande repart chez l'approbateur." : "Décrivez ce qu'il faut acheter et pourquoi. Les devis s'ajoutent ensuite, avant l'envoi pour approbation."}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4">
          <Card className="p-4 sm:p-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="req-object">Objet</Label>
              <Input id="req-object" value={form.object} maxLength={150} onChange={(e) => change({ object: e.target.value })} className="h-10" placeholder="Deux ordinateurs portables pour l'équipe support" data-testid="request-object" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="req-justification">Pourquoi cet achat est nécessaire</Label>
              <Textarea id="req-justification" rows={3} maxLength={1000} value={form.justification} onChange={(e) => change({ justification: e.target.value })} data-testid="request-justification" />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="req-needed">Nécessaire avant le (facultatif)</Label>
                <Input id="req-needed" type="date" value={form.needed_by} onChange={(e) => change({ needed_by: e.target.value })} className="h-10" data-testid="request-needed-by" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="req-project">Projet ou service (facultatif)</Label>
                <Input id="req-project" value={form.project} maxLength={120} onChange={(e) => change({ project: e.target.value })} className="h-10" data-testid="request-project" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="req-currency">Devise</Label>
                <Select value={form.currency} onValueChange={(currency) => change({ currency })} disabled={quotesLocked}>
                  <SelectTrigger id="req-currency" className="h-10" aria-label="Devise" data-testid="request-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{(currencies.includes(form.currency) ? currencies : [form.currency, ...currencies]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                {quotesLocked && <p className="text-xs text-slate-500">Retirez les devis pour changer de devise.</p>}
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-5 space-y-3" data-testid="request-lines">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-slate-900">Ce qu'il faut acheter</h2>
              <Button type="button" variant="outline" size="sm" onClick={() => change({ lines: [...form.lines, emptyLine()] })} disabled={form.lines.length >= 50} data-testid="request-line-add"><Plus size={14} className="mr-1.5" />Ajouter une ligne</Button>
            </div>
            <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_5rem_9rem_8rem_2.5rem] gap-2 text-xs text-slate-500 px-1">
              <span>Article</span><span className="text-right">Quantité</span><span className="text-right">Prix unitaire estimé</span><span className="text-right">Total</span><span />
            </div>
            {form.lines.map((line, index) => {
              const m = lineMinor(line, form.currency);
              return (
                <div key={line.key} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_5rem_9rem_8rem_2.5rem] sm:items-center rounded-md border border-slate-200 p-3 sm:border-0 sm:p-0" data-testid={`request-line-${index}`}>
                  <Input value={line.label} maxLength={200} onChange={(e) => setLine(index, { label: e.target.value })} className="h-10" placeholder="Dell Latitude 5440" aria-label={`Article de la ligne ${index + 1}`} data-testid={`request-line-label-${index}`} />
                  <div className="grid grid-cols-2 gap-2 sm:contents">
                    <Input inputMode="numeric" value={line.quantity} onChange={(e) => setLine(index, { quantity: e.target.value })} className="h-10 sm:text-right" placeholder="Qté" aria-label={`Quantité de la ligne ${index + 1}`} data-testid={`request-line-qty-${index}`} />
                    <Input inputMode="decimal" value={line.unit_price} onChange={(e) => setLine(index, { unit_price: e.target.value })} className="h-10 sm:text-right" placeholder={`Prix (${form.currency})`} aria-label={`Prix unitaire de la ligne ${index + 1}`} data-testid={`request-line-price-${index}`} />
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2">
                    <span className="text-sm tabular-nums text-slate-700">{m ? money(m.total, form.currency) : "—"}</span>
                    <Button type="button" variant="ghost" size="icon" className="sm:hidden text-slate-500" onClick={() => change({ lines: form.lines.filter((_, i) => i !== index) })} disabled={form.lines.length === 1} aria-label={`Retirer la ligne ${index + 1}`}><Trash2 size={16} /></Button>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="hidden sm:inline-flex text-slate-500" onClick={() => change({ lines: form.lines.filter((_, i) => i !== index) })} disabled={form.lines.length === 1} aria-label={`Retirer la ligne ${index + 1}`} data-testid={`request-line-remove-${index}`}><Trash2 size={16} /></Button>
                </div>
              );
            })}
          </Card>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <Card className="p-4 sm:p-5 space-y-3" data-testid="request-summary">
            <h2 className="font-semibold text-slate-900">Récapitulatif</h2>
            <div>
              <div className="text-xs text-slate-500">Total estimé</div>
              <div className="text-2xl font-semibold text-slate-900 tabular-nums" data-testid="request-total">{money(total, form.currency)}</div>
              <div className="text-xs text-slate-500">{form.lines.length} ligne{form.lines.length > 1 ? "s" : ""} · le montant à approuver est celui du devis retenu</div>
            </div>
            <ol className="text-sm text-slate-600 space-y-1.5 list-decimal pl-4">
              <li>Vous créez la demande.</li>
              <li>Vous ajoutez un ou plusieurs devis, et retenez celui que vous préférez.</li>
              <li>Vous l'envoyez : une autre personne l'approuve ou la refuse.</li>
              <li>L'achat est commandé, livré, facturé.</li>
            </ol>
            {error && <p className="text-sm text-red-600" role="alert" data-testid="request-form-error">{error}</p>}
            {problem && <p className="text-xs text-amber-700" data-testid="request-form-problem">{problem}</p>}
            <div className="flex flex-col-reverse sm:flex-row lg:flex-col-reverse gap-2">
              <Button type="button" variant="outline" asChild><Link to={editing ? `/achats/demandes/${requestId}` : "/achats/demandes"}>Annuler</Link></Button>
              <Button type="submit" disabled={busy || !!problem} data-testid="request-save">
                {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Créer la demande"}
              </Button>
            </div>
          </Card>
        </aside>
      </div>
    </form>
  );
}
